import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
} from "lola-framework-ui-test";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { REQUEST_ROUTES } from "../../constants";
import {
  getRequestMockReceipt,
} from "../../gallery/requestMockReceipts";
import { amountFormatter, formatHeroAmount, formatLabeledAmount } from "../../utils";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../../assets/icons";
import {
  STATUS_TONE_COLOR,
  requestToStatusPresentation,
  toStatusPresentation,
  type StatusTone,
} from "../../utils/statusPresentation";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { buildShareText, shareOrCopyMessage } from "../../utils/shareMessage";
import { Navbar } from "../../components/Navbar";

const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

const formatReceiptDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDate = (isoDate: string): string => formatReceiptDate(new Date(isoDate));

const formatReceiptDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  return `${formatReceiptDate(date)}\n${date.toLocaleTimeString()}`;
};

const availableReceiptDate = (isoDate: string): string => {
  const result = new Date(isoDate);
  let added = 0;
  while (added < 5) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return formatReceiptDate(result);
};

const money = (amount: number | undefined, currency: string): string =>
  formatLabeledAmount(amount ?? 0, currency);

/**
 * Request-flow receipt (gallery, requester perspective).
 * Resolves mock receipt data by `:requestId`.
 */
export const ReceiptSummaryPage = memo(() => {
  const { t, i18n } = useTranslation("request");
  const { theme } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("request");
  const location = useLocation();
  const { requestId: requestIdParam } = useParams<{ requestId: string }>();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors, footerColor } =
    colorConfig;

  const detail = useMemo(
    () => getRequestMockReceipt(requestIdParam, location.pathname),
    [requestIdParam, location.pathname]
  );

  const showShare =
    detail.requestStatus === "pending" && Boolean(detail.fundRequestUrl);
  const shareUrl = showShare ? (detail.fundRequestUrl ?? "") : "";

  const handleShare = async () => {
    if (!shareUrl) return;
    const message = buildShareText(t, {
      firstName: session.data?.userData?.name?.firstName,
      amount: detail.amount,
      currency: detail.destinationCurrency,
      url: shareUrl,
      language: i18n.language,
    });
    await shareOrCopyMessage(message, {
      title: t("share.shareTitle"),
      fallbackPrompt: t("share.manualPrompt"),
    });
  };

  const renderContent = () => {
    const hasOperation =
      detail.operationId != null &&
      detail.receiveAmount != null &&
      detail.sourceCurrency != null;

    const destination = detail.destinationCurrency;
    const source = detail.sourceCurrency ?? "USD";
    const heroAmount = hasOperation
      ? detail.receiveAmount!
      : (detail.amount ?? 0);
    const { prefix, suffix, intPart: heroInt, decPart: heroDec } = formatHeroAmount(
      heroAmount,
      destination
    );

    const presentation =
      hasOperation && detail.status
        ? toStatusPresentation(detail.status)
        : requestToStatusPresentation(detail.requestStatus);
    const labelKey =
      presentation.tone === "completed"
        ? "receipt.statusFunded"
        : presentation.labelKey;
    const statusColor = STATUS_TONE_COLOR[presentation.tone];
    const statusValue = t(labelKey);

    const summaryItems: {
      label: string;
      value: string;
      isBold?: boolean;
      statusColor?: string;
      icon?: React.ReactNode;
    }[] = hasOperation
      ? [
          {
            label: t("receipt.status"),
            value: statusValue,
            isBold: true,
            statusColor,
            icon: statusIcon(presentation.tone, statusColor),
          },
          {
            label: t("receipt.transferAmount"),
            value: money(detail.sendAmount, source),
          },
          {
            label: t("receipt.transactionFee"),
            value: money(detail.transactionFee, source),
          },
          {
            label: t("receipt.exchangeRate"),
            value: `${amountFormatter(detail.exchangeRate ?? 0, destination, {
              country: "en-US",
              minimumFractionDigits: 4,
              maximumFractionDigits: 4,
            })} ${destination} / ${source}`,
          },
          {
            label: t("receipt.totalPayment"),
            value: money(detail.totalCost, source),
          },
          {
            label: t("receipt.receiving"),
            value: money(detail.receiveAmount, destination),
          },
          {
            label: t("receipt.fundingFee"),
            value: money(
              detail.fundingFee ?? detail.fee ?? detail.totalFee,
              source
            ),
          },
          {
            label: t("receipt.transferTaxes"),
            value: money(detail.taxFee, source),
          },
          ...(detail.payerName
            ? [
                {
                  label: t("receipt.sender"),
                  value: detail.payerName,
                },
              ]
            : []),
          {
            label: t("receipt.transactionDate"),
            value: formatReceiptDateTime(detail.createdAt),
          },
          {
            label: t("receipt.dateAvailable"),
            value: availableReceiptDate(detail.createdAt),
          },
        ]
      : [
          {
            label: t("receipt.status"),
            value: statusValue,
            isBold: true,
            statusColor,
            icon: statusIcon(presentation.tone, statusColor),
          },
          {
            label: t("receipt.amountRequested"),
            value: money(detail.amount, destination),
          },
          {
            label: t("receipt.dateAvailable"),
            value: formatDate(detail.createdAt),
          },
        ];

    const idLabel = hasOperation
      ? t("receipt.transactionId")
      : t("receipt.requestId");
    const idValue = hasOperation ? detail.operationId : detail.requestId;

    return (
      <section className="receipt-summary-container">
        <section className="summary-section">
          <section className="summary-title">
            <BodyCopy textColor={bodyCopy}>
              {hasOperation
                ? t("receipt.youReceived")
                : t("receipt.youRequested")}
            </BodyCopy>
            <GradientText as="h1">
              {prefix}{heroInt}.<label>{heroDec}</label>
              {suffix ? (
                <span className="receipt-hero-currency">{suffix}</span>
              ) : null}
            </GradientText>
            <BodyCopy
              className="receipt-trx-caption"
              textColor={bodyCopy}
              aria-label={idLabel}
            >
              {(idValue ?? "").slice(0, 8).toUpperCase()}
            </BodyCopy>
          </section>
          <section className="summary-info">
            {summaryItems.map(({ label, value, isBold, statusColor: color, icon }, index) => (
              <SummaryInfoItem
                key={index}
                status={label}
                value={value}
                isBold={isBold}
                statusColor={color}
                icon={icon}
              />
            ))}
          </section>
        </section>
        <section className="receipt-summary-disclaimers">
          <BodyCopy className="footer-text" textColor={footerColor}>
            {t("receipt.footerNote")}
          </BodyCopy>
        </section>
      </section>
    );
  };

  return (
    <>
    <AuraLayout colorConfig={colorConfig} className="need-help-header receipt-summary-section">
      <Layout.Header>
        <Navbar
          title={t("common.needHelp")}
          align="right"
          onBackClick={() => navigate(REQUEST_ROUTES.HOME)}
          onIconClick={openSupport}
          icon={<SupportIcon size={35} colors={dropzoneColors} />}
        />
      </Layout.Header>
      <Layout.Content isOverflowauto className="receipt-summary-content">
        <ScrollFadeIndicator color={bodyCopy} />
        {renderContent()}
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section transactions-footer">
          {showShare && shareUrl && (
            <section className="receipt-share-cta">
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                onClick={() => void handleShare()}
              >
                {t("share.share")}
              </Button>
            </section>
          )}
        </section>
      </Layout.Footer>
    </AuraLayout>
    {supportSheet}
  </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
